import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import type { ThemeV2 } from '@modules/themes/types/themesV2.types';
import { ThemeV2PreviewCarousel } from '@modules/themes/components/ThemeV2PreviewCarousel';

export interface ThemeV2SelectionSource {
  id: string;
  label: string;
  themes: ThemeV2[];
  themeSource: string;
  chipLabel: string;
  chipColor?: React.ComponentProps<typeof Chip>['color'];
}

export interface ThemeV2SelectionListProps {
  sources: ThemeV2SelectionSource[];
  selectedThemeId?: string | null;
  /** The theme already in use: its card carries an outlined "Current" chip. */
  currentThemeId?: string | null;
  projectId?: string;
  loading?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  loadMoreDisabled?: boolean;
  emptyMessage?: string;
  cardTestIdPrefix?: string;
  showSourceHeaders?: boolean;
  onSelect: (theme: ThemeV2) => void;
}

export const ThemeV2SelectionList: React.FC<ThemeV2SelectionListProps> = ({
  sources,
  selectedThemeId,
  currentThemeId = null,
  projectId,
  loading = false,
  hasMore = false,
  loadMore,
  loadMoreDisabled = false,
  emptyMessage = 'No V2 themes available. Create a V2 theme first.',
  cardTestIdPrefix = 'wizard-v2-theme-card-',
  showSourceHeaders = true,
  onSelect,
}) => {
  const visibleSources = sources.filter((source) => source.themes.length > 0);
  const themeCount = visibleSources.reduce((count, source) => count + source.themes.length, 0);

  // The theme in use may be far down a long list: bring its card into view once the list has loaded.
  const currentCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    currentCardRef.current?.scrollIntoView?.({ block: 'center' });
  }, [currentThemeId, themeCount]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        overflow: 'auto',
      }}
    >
      {visibleSources.map((source) => (
        <Box
          key={source.id}
          data-testid={`theme-v2-selection-source-${source.id}`}
          data-theme-source={source.themeSource}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          {showSourceHeaders && sources.length > 1 && (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              data-testid={`theme-v2-selection-source-title-${source.id}`}
              sx={{ px: 0.5 }}
            >
              {source.label}
            </Typography>
          )}

          {source.themes.map((theme) => {
            const isSelected = selectedThemeId === theme.id;
            const isCurrent = currentThemeId !== null && currentThemeId === theme.id;

            return (
              <Card
                key={theme.id}
                ref={isCurrent ? currentCardRef : undefined}
                role="button"
                onClick={() => onSelect(theme)}
                data-testid={`${cardTestIdPrefix}${theme.id}`}
                data-theme-source={source.themeSource}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  p: 2,
                  cursor: 'pointer',
                  height: 380,
                  minHeight: 380,
                  maxHeight: 380,
                  overflow: 'hidden',
                  border: isSelected ? 2 : 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  '&:hover': {
                    borderColor: 'primary.light',
                  },
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => onSelect(theme)}
                  onClick={(event) => event.stopPropagation()}
                />
                <Box sx={{ flex: 1, ml: 2, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                      {theme.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={source.chipLabel}
                      color={source.chipColor ?? 'default'}
                    />
                    {isCurrent && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Current"
                        data-testid="theme-v2-selection-current-chip"
                      />
                    )}
                  </Stack>
                  <Box sx={{ height: 310, overflowX: 'auto', overflowY: 'hidden' }}>
                    <ThemeV2PreviewCarousel theme={theme} compact projectId={projectId} />
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      ))}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {hasMore && !loadMoreDisabled && loadMore && (
        <Button
          onClick={loadMore}
          variant="text"
          fullWidth
          sx={{ mt: 1 }}
        >
          Load more themes
        </Button>
      )}

      {themeCount === 0 && !loading && (
        <Typography color="text.secondary" textAlign="center" py={4}>
          {emptyMessage}
        </Typography>
      )}
    </Box>
  );
};

export default ThemeV2SelectionList;
